<!--
 * @Description: 编辑工具组件（支持 Swiper 轮播和换行两种模式，换行模式支持动态列宽）
-->
<template>
    <div>
        <!-- 标题区域 -->
        <div class="flex items-center justify-between mb-5 mt-10">
            <h2 class="text-white text-base font-bold">
                {{ t('homeView.ai_tools') }}
            </h2>
            <!-- 移动端查看全部 -->
            <NuxtLink to="/ai-tools" class="lg:hidden text-xs text-[#adadad] hover:text-white transition-colors">
                {{ t('menus.viewAll') }} >
            </NuxtLink>
        </div>
        <!-- 轮播模式 -->
        <UiInfiniteCarousel v-if="!wrapMode" :items="toolsList" :autoplay-interval="0" :scroll-step="2" :gap="0"
            :get-item-key="(item: any) => item.video" @item-click="handleToolClick">
            <template #default="{ item, isClone }">
                <div class="w-[140px] max-md:mr-3 lg:w-[271px] flex-shrink-0">
                    <div
                        class="w-[140px] h-[190px] lg:w-[271px] lg:h-[366px] relative overflow-hidden mb-3 rounded-[5px] lg:rounded-[20px] group cursor-pointer">
                        <template v-if="item.video">
                            <!-- 性能优化：避免 InfiniteCarousel 的克隆区重复渲染/播放视频 -->
                            <video v-if="!isClone" v-autoplay-visible
                                class="absolute inset-0 size-full object-cover group-hover:scale-110 rounded-[5px] lg:rounded-[20px] transition-all duration-300"
                                :poster="getLocalizedMedia(item).thumbnail
                                    || getResourceThumbnail(
                                        getLocalizedMedia(item).video
                                    )
                                    " :src="getLocalizedMedia(item).video" loop muted playsinline webkit-playsinline
                                preload="metadata" crossorigin="anonymous"></video>
                            <!-- 克隆项用 poster 图代替，避免创建额外 video 解码器 -->
                            <img v-else
                                class="absolute inset-0 size-full object-cover group-hover:scale-110 rounded-[5px] lg:rounded-[20px] transition-all duration-300"
                                :src="getLocalizedMedia(item).thumbnail || getResourceThumbnail(
                                    getLocalizedMedia(item).video
                                )
                                    " :alt="item.title" loading="lazy" decoding="async" />
                        </template>
                        <img v-else
                            class="absolute inset-0 size-full object-cover group-hover:scale-110 rounded-[5px] lg:rounded-[20px] transition-all duration-300"
                            :src="getLocalizedMedia(item).thumbnail" :alt="item.title" loading="lazy"
                            decoding="async" />
                        <!-- 标签 -->
                        <div class="absolute top-2 right-2 flex gap-1 z-10">
                            <span v-if="item.is_new"
                                class="px-2 py-0.5 text-[10px] text-white bg-[#00AC78] rounded-full">{{
                                t('homeView.new')
                                }}</span>
                            <span v-if="item.is_audio"
                                class="px-2 py-0.5 text-[10px] text-white bg-[#B91C1C] rounded-full">{{
                                t('homeView.audio') }}</span>
                            <span v-if="item.is_image"
                                class="px-2 py-0.5 text-[10px] text-white bg-[#B91C1C] rounded-full">{{
                                t('homeView.image') }}</span>
                        </div>
                    </div>
                    <p class="text-white w-full font-normal text-[16px] truncate mb-1">
                        {{ item.title }}
                    </p>
                    <p class="text-[#ADADAD] w-full text-[13px] font-normal line-clamp-1">
                        {{ item.desc }}
                    </p>
                </div>
            </template>
        </UiInfiniteCarousel>

        <template v-else>
            <div class="md:hidden grid grid-cols-2 gap-4">
                <div v-for="item in toolsList" :key="item.path" class="ai-tool-item cursor-pointer group min-h-[300px]"
                    @click="handleToolClick(item)">
                    <video v-if="item.video" v-autoplay-visible class="media-content" :poster="getLocalizedMedia(item).thumbnail || getResourceThumbnail(
                        getLocalizedMedia(item).video
                    )
                        " :src="getLocalizedMedia(item).video" loop muted playsinline webkit-playsinline
                        preload="metadata" />
                    <img v-else loading="lazy" class="media-content" :src="getLocalizedMedia(item).thumbnail || getResourceThumbnail(
                        getLocalizedMedia(item).video
                    )
                        " :alt="item.title" />
                </div>
            </div>
            <!-- 换行模式（动态列宽） -->
            <div ref="gridContainerRef" class="tools-wrap-grid hidden md:flex">
                <div v-for="item in toolsList" :key="item.path" class="ai-tool-item cursor-pointer group"
                    :style="{ width: `${columnWidth}px` }" @click="handleToolClick(item)">
                    <div class="media-box mb-3" :style="{
                        width: `${columnWidth}px`,
                        height: `${itemHeight}px`,
                    }">
                        <video v-if="item.video" class="media-content" :poster="item.thumbnail || getResourceThumbnail(item.video)
                            " :src="item.video" loop autoplay muted playsinline webkit-playsinline
                            preload="metadata" />
                        <img v-else loading="lazy" class="media-content" :src="item.thumbnail || getResourceThumbnail(item.video)
                            " :alt="item.title" />
                    </div>
                    <p class="text-white font-normal text-[16px] truncate mb-1"
                        style="font-family: 'Roboto', sans-serif">
                        {{ item.title }}
                    </p>
                    <p class="text-[#ADADAD] text-[13px] font-normal leading-normal line-clamp-2"
                        style="font-family: 'Roboto', sans-serif">
                        {{ item.desc }}
                    </p>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useUserStore } from '~/stores/user'
import { useToolsHooks } from '~/utils/useHooks'
import { emitOpenPromptReversalModal } from '~/utils/emitter'
import { getResourceThumbnail } from '~/utils/common'

const props = defineProps<{
    toolsList?: any[]
    wrapMode?: boolean
}>()

const { t, locale } = useI18n()

const getLocalizedMedia = (item: any) => {
    const localized = item?.[locale.value]
    return {
        thumbnail: localized?.thumbnail || item?.thumbnail || '',
        video: localized?.video || item?.video || '',
    }
}

const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const router = useRouter()

const toolsHooks = useToolsHooks()
const aiHotToolsEnums = toolsHooks.aiHotToolsEnums

const defaultToolsList = computed(() => aiHotToolsEnums.value)

const toolsList = computed(() => {
    if (props.toolsList && props.toolsList.length > 0) {
        return props.toolsList
    }

    return defaultToolsList.value
})

const wrapMode = computed(() => props.wrapMode ?? false)

// 视频预览：使用 v-autoplay-visible（可见时自动播放、离屏自动暂停），避免滚动后累积大量后台解码

// ===== 动态列宽配置（与 InspirationWaterFull 保持一致） =====
const config = {
    baseColumnWidth: 160,
    maxColumnWidth: 280,
    minColumnWidth: 140,
    gap: 12,
    minColumns: 1,
}
const aspectRatio = 271 / 366 // 保持原有宽高比

const gridContainerRef = ref<HTMLElement | null>(null)
const columnWidth = ref(config.baseColumnWidth)
const itemHeight = computed(() => Math.ceil(columnWidth.value / aspectRatio))

// 计算动态列宽
const calculateColumnWidth = (containerWidthOverride?: number) => {
    if (!gridContainerRef.value && !containerWidthOverride) return

    const containerWidth =
        containerWidthOverride ?? gridContainerRef.value!.offsetWidth

    // 按基准列宽计算列数
    let columns = Math.floor(
        (containerWidth + config.gap) / (config.baseColumnWidth + config.gap)
    )
    columns = Math.max(columns, config.minColumns)

    // 根据列数反推最佳列宽
    let width = (containerWidth - (columns - 1) * config.gap) / columns

    // 限制列宽范围
    width = Math.max(
        config.minColumnWidth,
        Math.min(config.maxColumnWidth, width)
    )

    columnWidth.value = width
}

// ResizeObserver: 用 rAF 合帧，避免 ResizeObserver loop 警告
let resizeRafId = 0
let lastObservedWidth = 0

const scheduleResize = (width?: number) => {
    if (typeof width === 'number') {
        const rounded = Math.max(0, Math.round(width))
        if (rounded === lastObservedWidth) return
        lastObservedWidth = rounded
    }

    if (resizeRafId) cancelAnimationFrame(resizeRafId)
    resizeRafId = requestAnimationFrame(() => {
        resizeRafId = 0
        calculateColumnWidth(lastObservedWidth || undefined)
    })
}

let resizeObserver: ResizeObserver | null = null

const setupResizeObserver = () => {
    if (gridContainerRef.value) {
        calculateColumnWidth()
        resizeObserver = new ResizeObserver(entries => {
            const entry = entries[0]
            if (!entry) return
            scheduleResize(entry.contentRect.width)
        })
        resizeObserver.observe(gridContainerRef.value)
    }
}

onMounted(() => {
    if (wrapMode.value) {
        setupResizeObserver()
    }
})

// 监听 wrapMode 变化，动态设置 observer
watch(wrapMode, newVal => {
    if (newVal) {
        // 切换到换行模式时，等待 DOM 更新后设置 observer
        setTimeout(setupResizeObserver, 0)
    } else {
        // 切换到轮播模式时，移除 observer
        if (resizeObserver) {
            resizeObserver.disconnect()
            resizeObserver = null
        }
    }
})

onUnmounted(() => {
    if (resizeObserver) resizeObserver.disconnect()
    if (resizeRafId) cancelAnimationFrame(resizeRafId)
})

const handleToolClick = async (item: any) => {
    if (item.isModal) {
        emitOpenPromptReversalModal()
        return
    }
    if (!isLoggedIn.value) {
        // 未登录，跳转介绍页
        router.push(item.path)
    } else {
        // 已登录，如果是模型（有 supportType），跳转到对应页面并设置默认模型
        if (item.supportType && item.model_key) {
            router.push(
                `/${item.supportType[0]}?defaultModel=${item.model_key}${item.version ? '&version=' + item.version : ''
                }`
            )
        } else {
            // 如果是工具，直接跳转到工具页面
            router.push(item.path)
        }
    }
}
</script>

<style lang="less" scoped>
:deep(.left-arrow) {
    left: 10px;
    width: 32px;
    height: 32px;
    background-size: 18px 18px;

    @media (min-width: 1024px) {
        left: 0;
        width: 40px;
        height: 40px;
        background-size: 24px 24px;
    }
}

:deep(.right-arrow) {
    right: 10px;
    width: 32px;
    height: 32px;
    background-size: 18px 18px;

    @media (min-width: 1024px) {
        right: 0;
        width: 40px;
        height: 40px;
        background-size: 24px 24px;
    }
}

.ai-tools-swiper {

    :deep(.swiper-button-prev),
    :deep(.swiper-button-next) {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-image: url('~/assets/images/common/right-arrow.svg');
        background-size: 24px 24px;
        background-repeat: no-repeat;
        background-position: center;
        background-color: rgb(0 0 0 / 0.4);
        transition: all 0.3s ease;
        color: transparent;

        &::after {
            content: '' !important;
            display: none !important;
        }

        &:hover {
            transform: scale(1.1);
        }
    }

    :deep(.swiper-button-prev) {
        rotate: 180deg;
        left: 0;
    }

    :deep(.swiper-button-next) {
        right: 0;
    }

    :deep(.swiper-slide) {
        width: auto !important;
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
    }
}

.ai-tool-item {
    transition: transform 0.2s ease;
    background: transparent;
    border: none;

    &:hover {
        transform: translateY(-4px);
        background: transparent;
        border: none;
    }
}

.tools-wrap-grid {
    flex-wrap: wrap;
    gap: 16px;
    justify-content: flex-start;
    box-sizing: border-box;
}

.media-box {
    position: relative;
    overflow: hidden;
    border-radius: 10px;
    background: #000;
}

.media-content {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.ai-tool-item:hover .media-content {
    transform: scale(1.05);
}
</style>
